import React, { useState, useCallback } from "react";

import Button from "../../components/design-system/Button";
import { cn } from "@faire/prototype-playground/lib/utils";

interface Category {
  id: string;
  label: string;
  children?: Category[];
}

const PAGE_1_CATEGORIES: Category[] = [
  {
    id: "home-decor",
    label: "Home décor",
    children: [
      { id: "home-candles", label: "Candles & holders" },
      { id: "home-wall-art", label: "Wall art" },
      { id: "home-vases", label: "Vases & planters" },
    ],
  },
  {
    id: "jewelry",
    label: "Jewelry",
    children: [
      { id: "jewelry-necklaces", label: "Necklaces" },
      { id: "jewelry-earrings", label: "Earrings" },
      { id: "jewelry-bracelets", label: "Bracelets" },
    ],
  },
  {
    id: "apparel",
    label: "Apparel",
    children: [
      { id: "apparel-tops", label: "Tops" },
      { id: "apparel-dresses", label: "Dresses" },
      { id: "apparel-outerwear", label: "Outerwear" },
    ],
  },
  {
    id: "skincare",
    label: "Skincare",
    children: [
      { id: "skincare-moisturizers", label: "Moisturizers" },
      { id: "skincare-serums", label: "Serums" },
      { id: "skincare-cleansers", label: "Cleansers" },
    ],
  },
  {
    id: "food-drink",
    label: "Food & drink",
    children: [
      { id: "food-snacks", label: "Snacks" },
      { id: "food-chocolate", label: "Chocolate" },
      { id: "food-beverages", label: "Beverages" },
    ],
  },
  {
    id: "accessories",
    label: "Accessories",
    children: [
      { id: "accessories-bags", label: "Bags" },
      { id: "accessories-hats", label: "Hats" },
      { id: "accessories-scarves", label: "Scarves" },
    ],
  },
  {
    id: "stationery",
    label: "Stationery",
    children: [
      { id: "stationery-cards", label: "Cards" },
      { id: "stationery-journals", label: "Journals" },
      { id: "stationery-giftwrap", label: "Gift wrap" },
    ],
  },
  {
    id: "kitchen",
    label: "Kitchen & tabletop",
    children: [
      { id: "kitchen-serveware", label: "Serveware" },
      { id: "kitchen-barware", label: "Barware" },
      { id: "kitchen-linens", label: "Linens" },
    ],
  },
  {
    id: "bath-body",
    label: "Bath & body",
    children: [
      { id: "bath-soaps", label: "Soaps" },
      { id: "bath-lotions", label: "Lotions" },
      { id: "bath-bombs", label: "Bath bombs" },
    ],
  },
];

const PAGE_2_CATEGORIES: Category[] = [
  {
    id: "wellness",
    label: "Wellness",
    children: [
      { id: "wellness-supplements", label: "Supplements" },
      { id: "wellness-aromatherapy", label: "Aromatherapy" },
      { id: "wellness-relaxation", label: "Relaxation" },
    ],
  },
  {
    id: "baby-kids",
    label: "Baby & kids",
    children: [
      { id: "kids-toys", label: "Toys" },
      { id: "kids-clothing", label: "Clothing" },
      { id: "kids-nursery", label: "Nursery" },
    ],
  },
  {
    id: "garden",
    label: "Garden & outdoor",
    children: [
      { id: "garden-tools", label: "Tools" },
      { id: "garden-pots", label: "Pots & planters" },
      { id: "garden-decor", label: "Outdoor décor" },
    ],
  },
  {
    id: "pets",
    label: "Pets",
    children: [
      { id: "pets-treats", label: "Treats" },
      { id: "pets-toys", label: "Toys" },
      { id: "pets-grooming", label: "Grooming" },
    ],
  },
  {
    id: "books",
    label: "Books",
    children: [
      { id: "books-fiction", label: "Fiction" },
      { id: "books-nonfiction", label: "Non-fiction" },
      { id: "books-childrens", label: "Children's" },
    ],
  },
  {
    id: "fitness",
    label: "Fitness & yoga",
    children: [
      { id: "fitness-mats", label: "Mats" },
      { id: "fitness-accessories", label: "Accessories" },
      { id: "fitness-apparel", label: "Apparel" },
    ],
  },
  {
    id: "craft",
    label: "Craft supplies",
    children: [
      { id: "craft-yarn", label: "Yarn & fiber" },
      { id: "craft-beading", label: "Beading" },
      { id: "craft-kits", label: "DIY kits" },
    ],
  },
  {
    id: "seasonal",
    label: "Seasonal & holiday",
    children: [
      { id: "seasonal-ornaments", label: "Ornaments" },
      { id: "seasonal-decor", label: "Décor" },
      { id: "seasonal-gifting", label: "Gifting" },
    ],
  },
];

function ChevronDown({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      className={cn("ml-1 transition-transform duration-200", expanded ? "rotate-180" : "")}
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CategoryChip({
  label,
  selected,
  expanded,
  hasChildren,
  onClick,
  disabled,
}: {
  label: string;
  selected: boolean;
  expanded?: boolean;
  hasChildren?: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const base =
    "inline-flex items-center justify-center px-4 py-2 rounded-full text-[14px] tracking-[0.15px] transition-all duration-200 cursor-pointer select-none";

  let state: string;
  if (selected || expanded) {
    state = "bg-[#333] text-fs-text-primary-inverse";
  } else {
    state = "bg-fs-surface-primary border border-[#dfe0e1] text-[#333] hover:border-[#999]";
  }

  if (disabled && !selected) {
    state += " opacity-40 cursor-not-allowed";
  }

  return (
    <button
      type="button"
      onClick={disabled && !selected ? undefined : onClick}
      className={cn(base, state)}
      style={{ fontFamily: "'Graphik', sans-serif", lineHeight: "20px" }}
    >
      {selected && !hasChildren && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          className="mr-1.5"
        >
          <path
            d="M2 6L5 9L10 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label}
      {hasChildren && <ChevronDown expanded={!!expanded} />}
    </button>
  );
}

function SubcategoryChip({
  label,
  selected,
  onClick,
  disabled,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const base =
    "inline-flex items-center justify-center px-3.5 py-1.5 rounded-full text-[13px] tracking-[0.15px] transition-all duration-200 cursor-pointer select-none";

  let state: string;
  if (selected) {
    state = "bg-[#555] text-fs-text-primary-inverse";
  } else {
    state =
      "bg-fs-surface-primary border border-dashed border-[#bbb] text-[#555] hover:border-[#888]";
  }

  if (disabled && !selected) {
    state += " opacity-40 cursor-not-allowed";
  }

  return (
    <button
      type="button"
      onClick={disabled && !selected ? undefined : onClick}
      className={cn(base, state)}
      style={{ fontFamily: "'Graphik', sans-serif", lineHeight: "18px" }}
    >
      {selected && (
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          className="mr-1"
        >
          <path
            d="M2 6L5 9L10 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label}
    </button>
  );
}

export default function Variant4() {
  const [page, setPage] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedParents, setExpandedParents] = useState<Set<string>>(
    new Set()
  );

  const totalSelections = selectedIds.size;

  const toggleCategory = useCallback(
    (category: Category) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);

        if (category.children) {
          if (expandedParents.has(category.id)) {
            setExpandedParents((ep) => {
              const n = new Set(ep);
              n.delete(category.id);
              return n;
            });
            next.delete(category.id);
            category.children.forEach((c) => next.delete(c.id));
          } else {
            setExpandedParents((ep) => new Set(ep).add(category.id));
            next.add(category.id);
          }
        } else {
          if (next.has(category.id)) {
            next.delete(category.id);
          } else {
            next.add(category.id);
          }
        }

        return next;
      });
    },
    [expandedParents]
  );

  const toggleSubcategory = useCallback((child: Category) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(child.id)) {
        next.delete(child.id);
      } else {
        next.add(child.id);
      }
      return next;
    });
  }, []);

  const currentCategories = page === 1 ? PAGE_1_CATEGORIES : PAGE_2_CATEGORIES;

  const pageTitle =
    page === 1
      ? "What are you looking for on Faire right now?"
      : "We have lots of categories — anything else catch your eye?";
  const pageSubtitle =
    page === 1
      ? "Pick the categories you're actively shopping for."
      : "These are popular with retailers who expand beyond their core.";
  const sectionLabel =
    page === 1 ? "Shopping for now" : "You might also love";

  return (
    <div className="flex min-h-screen items-start justify-center bg-[#f5f5f5] py-8">
      <div
        className="flex flex-col overflow-hidden rounded-3xl bg-fs-surface-primary shadow-lg"
        style={{ width: 375, height: 812 }}
      >
        {/* Nav bar */}
        <div className="relative flex shrink-0 items-center justify-center px-6 py-4">
          <button
            type="button"
            className="absolute left-2 p-3.5 text-[#333]"
            aria-label="Back"
            onClick={() => {
              if (page === 2) {
                setExpandedParents(new Set());
                setPage(1);
              }
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 3L5 8L10 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span
            className="text-base tracking-[0.35em] text-[#333]"
            style={{ fontFamily: "'Graphik', sans-serif", fontWeight: 400 }}
          >
            F A I R E
          </span>
        </div>

        {/* Progress bar */}
        <div className="flex w-full shrink-0">
          <div
            className="h-1 bg-[#333] transition-all duration-500 ease-in-out"
            style={{ width: page === 1 ? "50%" : "100%" }}
          />
          <div className="h-1 flex-1 bg-[#f7f7f7]" />
        </div>

        {/* Page content */}
        <div className="flex-1 overflow-y-auto px-6 pt-8 pb-6">
          {/* Step indicator */}
          <div
            className="mb-2 text-[11px] font-medium uppercase tracking-[1.2px] text-[#999]"
            style={{ fontFamily: "'Graphik', sans-serif" }}
          >
            Step {page} of 2
          </div>

          <h1
            className="mb-2 text-[26px] leading-[34px] text-[#333]"
            style={{ fontFamily: "'Nantes', serif" }}
          >
            {pageTitle}
          </h1>
          <p
            className="mb-6 text-sm leading-5 tracking-[0.15px] text-[#757575]"
            style={{ fontFamily: "'Graphik', sans-serif" }}
          >
            {pageSubtitle}
          </p>

          {/* Section label */}
          <div className="mb-3 flex items-center gap-2">
            {page === 1 ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="text-[#999]"
              >
                <path
                  d="M7 1.5L3 5.5V10.5C3 11.05 3.45 11.5 4 11.5H10C10.55 11.5 11 11.05 11 10.5V5.5L7 1.5Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.5 11.5V8C5.5 7.45 5.95 7 6.5 7H7.5C8.05 7 8.5 7.45 8.5 8V11.5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="text-[#999]"
              >
                <path
                  d="M7 1L8.5 4.6L12.5 5L9.75 7.5L10.5 11.5L7 9.5L3.5 11.5L4.25 7.5L1.5 5L5.5 4.6L7 1Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            <span
              className="text-[11px] font-medium uppercase tracking-[0.8px] text-[#757575]"
              style={{ fontFamily: "'Graphik', sans-serif" }}
            >
              {sectionLabel}
            </span>
            {totalSelections > 0 && (
              <span
                className="rounded-full bg-[#333] px-1.5 py-0.5 text-[10px] font-medium text-fs-text-primary-inverse"
                style={{ fontFamily: "'Graphik', sans-serif" }}
              >
                {totalSelections}
              </span>
            )}
          </div>

          {/* Expandable chips */}
          <div className="flex flex-wrap gap-2">
            {currentCategories.map((cat) => {
              const isExpanded = expandedParents.has(cat.id);
              const isSelected = selectedIds.has(cat.id);

              return (
                <React.Fragment key={cat.id}>
                  <CategoryChip
                    label={cat.label}
                    selected={isSelected}
                    expanded={isExpanded}
                    hasChildren={!!cat.children}
                    onClick={() => toggleCategory(cat)}
                  />

                  {isExpanded &&
                    cat.children?.map((child) => (
                      <SubcategoryChip
                        key={child.id}
                        label={child.label}
                        selected={selectedIds.has(child.id)}
                        onClick={() => toggleSubcategory(child)}
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="shrink-0 border-t border-[#f0f0f0] bg-fs-surface-primary px-6 py-4">
          <div className="flex items-center gap-3">
            {page === 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setExpandedParents(new Set());
                    setPage(2);
                  }}
                  className="flex-1 py-2.5 text-center text-sm text-[#757575] transition-colors hover:text-[#333]"
                  style={{ fontFamily: "'Graphik', sans-serif" }}
                >
                  Skip for now
                </button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => {
                    setExpandedParents(new Set());
                    setPage(2);
                  }}
                >
                  Next
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    alert(
                      `Selected:\n${Array.from(selectedIds).join("\n") || "(none)"}`
                    );
                  }}
                  className="flex-1 py-2.5 text-center text-sm text-[#757575] transition-colors hover:text-[#333]"
                  style={{ fontFamily: "'Graphik', sans-serif" }}
                >
                  Skip for now
                </button>
                <Button
                  variant="primary"
                  disabled={totalSelections === 0}
                  className="flex-1"
                  onClick={() => {
                    alert(
                      `Selected:\n${Array.from(selectedIds).join("\n")}`
                    );
                  }}
                >
                  Done
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
